import { call, put, select, takeEvery } from 'redux-saga/effects';
import getSagaErrorHandler from 'utils/getSagaErrorHandler';
import getData from 'utils/getData';
import uiActions from 'core/ui/actions';
import pageActions from 'core/page/actions';
import plaidActions from 'core/plaid/actions';
import analyticsActions from 'core/analytics/actions';
import dataActions from 'core/data/actions';
import groupActions from './actions';

const FUNDING_MAP = {
  AUTOMATIC_DEBIT: 'FUNDING_TRANSFER_EXT',
  CHECK: 'CHECK',
  INTERNAL_TRANSFER: 'FUNDING_TRANSFER_INT',
  MOBILE_WALLET: 'FUNDING_MOBILE_WALLET',
  PAYMENT_CARD: 'FUNDING_CC',
};

function* groupErrorSaga(e) {
  const {
    response: { data, status },
  } = e;
  if (status === 400) {
    if (
      data.result.msgs &&
      data.result.msgs['*'] &&
      data.result.msgs['*'] === 'ANSWER_VERIFICATION_QUESTIONS'
    ) {
      yield put(plaidActions.handleVerificationQuestions(data));
    }
    yield put(groupActions.nextFailed(data));
  } else {
    yield put(pageActions.goToPageError(data, status));
  }
}

const wrapGroupSagaWithErrorHandler = getSagaErrorHandler(groupErrorSaga);

const getNextGroup = (name) => {
  switch (name) {
    case 'PRIMARY_DL':
      return 'PRIMARY_PII';
    case 'SECONDARY_DL':
      return 'SECONDARY_PII';
    case 'SIGNER_DL':
      return 'SIGNER_PII';
    default:
      return undefined;
  }
};

function* next({ payload }) {
  const { data, fields: stateFields, groups, page, session } = yield select();
  const group = groups[payload.group];
  const groupFields =
    (group.fields &&
      group.fields.reduce((gfs, fieldKey) => {
        if (stateFields[fieldKey] && stateFields[fieldKey].fieldType === 'LOOKUP_COMPOSITE') {
          const map = stateFields[fieldKey].lookupFieldMap;
          return [...gfs, ...Object.keys(map).reduce((a, k) => [...a, ...map[k]], []), fieldKey];
        }
        return [...gfs, fieldKey];
      }, [])) ||
    [];

  // Add list groups
  if (data[`${group.name}___list`]) {
    groupFields.push(`${group.name}___list`);
  }

  const nextGroup = getNextGroup(group.name);

  let token;
  if (group.recaptchaEnabled && window.grecaptcha) {
    token = yield call(() =>
      window.grecaptcha.execute(session.meta.configs.google_recaptcha_site_key, {
        action: 'group_submit',
      }),
    );
  }

  const response = yield call(
    payload.request(
      page.name,
      groupFields.reduce(
        (groupData, field) => ({
          ...groupData,
          [field]: data[field],
          ...(token && { appRequest_recaptcha: token }),
        }),
        {},
      ),
    ),
  );
  // nextSuccess changes the active group, so call Analytics first
  yield put(analyticsActions.oaoGroupSubmitSuccess(group.name));
  yield put(groupActions.nextSuccess(response.data, nextGroup));
}

function* update({ payload }) {
  const { data, fields: stateFields, groups, page, session } = yield select();
  const group = groups[payload.group];
  const groupFields =
    (group.fields &&
      group.fields.reduce((gfs, fieldKey) => {
        if (stateFields[fieldKey] && stateFields[fieldKey].fieldType === 'LOOKUP_COMPOSITE') {
          const map = stateFields[fieldKey].lookupFieldMap;
          return [...gfs, ...Object.keys(map).reduce((a, k) => [...a, ...map[k]], []), fieldKey];
        }
        return [...gfs, fieldKey];
      }, [])) ||
    [];

  // Add list groups
  if (data[`${group.name}___list`]) {
    groupFields.push(`${group.name}___list`);
  }

  let token;
  if (group.recaptchaEnabled && window.grecaptcha) {
    token = yield call(() =>
      window.grecaptcha.execute(session.meta.configs.google_recaptcha_site_key, {
        action: 'group_submit',
      }),
    );
  }

  const response = yield call(
    payload.request(
      page.name,
      groupFields.reduce(
        (groupData, field) => ({
          ...groupData,
          [field]: data[field],
          ...(token && { appRequest_recaptcha: token }),
        }),
        {},
      ),
    ),
  );
  yield put(groupActions.updateSuccess(response.data));

  if (payload.group === 'PRIMARY_USER') {
    yield put(dataActions.changeData('isEditing', false));
  } else {
    yield put(
      dataActions.changeListGroupData(`${payload.group}___list`, payload.index, 'isEditing', false),
    );
  }
}

const getActivePanel = (panel) => {
  switch (panel) {
    case null:
    case 'back':
      return 'front';
    case 'front':
      return 'summary';
    default:
      return 'back';
  }
};

function* upload({ payload }) {
  const { page, ui } = yield select();
  const { files } = payload;
  const formData = new FormData();

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];

    formData.append('file', file, file.name);
  }

  try {
    const response = yield call(payload.request(page.name, ui.activeGroup, formData));
    const { data } = response;
    yield put(groupActions.uploadSuccess(data, getActivePanel(ui.activePanel)));
  } catch (e) {
    const {
      response: { data, status },
    } = e;
    if (status === 400) {
      yield put(groupActions.uploadFailed(data));
    } else {
      yield put(pageActions.goToPageError(data, status));
    }
  }
}

function* back() {
  const { groups, ui } = yield select();
  const groupKeys = Object.keys(groups);
  const indexOfCurrentGroup = groupKeys.indexOf(ui.activeGroup);

  if (indexOfCurrentGroup > 0) {
    yield put(uiActions.setActiveGroup(groupKeys[indexOfCurrentGroup - 1]));
  } else {
    yield put(pageActions.back());
  }
}

function* apiAction({ payload }) {
  const { page } = yield select();
  const formData = yield select(getData);

  const response = yield call(payload.request(page.name, formData));
  const { data } = response;
  yield put(groupActions.nextSuccess(data, payload.nextGroup));
}

function* save({ payload }) {
  const { page } = yield select();
  const formData = yield select(getData);
  yield call(payload.request(page.name, formData));
  yield put(pageActions.goToNonWorkflowPage('home'));
}

function* setActiveGroup({ payload }) {
  const { activeGroup } = yield select((state) => state.ui);
  const { data, nextGroup } = payload;
  const groupKeys = Object.keys(data.meta.groups);
  const currentIndexOfGroup = groupKeys.indexOf(activeGroup);
  const group =
    nextGroup ||
    FUNDING_MAP[data.data.appRequest_fundingMethod] ||
    groupKeys[currentIndexOfGroup + 1];

  if (group && !data.meta.nextPage) {
    yield put(uiActions.setActiveGroup(group));
  }
}

function* prodStartNext() {
  const { productLookup } = yield select((state) => state.data);
  const { marketIdentifier, marketName } = yield select(
    (state) => state.groups.PROD_START.resources,
  );

  const market = marketIdentifier && marketName ? `&${marketIdentifier}=${marketName}` : '';
  window.location.assign(`/oao/?product=${productLookup}${market}`);
}

export default [
  takeEvery([groupActions.NEXT, plaidActions.SAVE_QUESTIONS], wrapGroupSagaWithErrorHandler(next)),
  takeEvery(groupActions.UPLOAD, upload),
  takeEvery(groupActions.BACK, back),
  takeEvery(groupActions.API_ACTION, wrapGroupSagaWithErrorHandler(apiAction)),
  takeEvery(groupActions.UPDATE, wrapGroupSagaWithErrorHandler(update)),
  takeEvery(groupActions.SAVE, wrapGroupSagaWithErrorHandler(save)),
  takeEvery(groupActions.NEXT_SUCCESS, wrapGroupSagaWithErrorHandler(setActiveGroup)),
  takeEvery(groupActions.PROD_START_NEXT, wrapGroupSagaWithErrorHandler(prodStartNext)),
];
